import React from 'react';
import risingSquadLogo from '@/assets/rising-squad.png';
import { useStuckLoadingDetector } from '@/hooks/useStuckLoadingDetector';

/**
 * AppLoadingSpinner Component
 * 
 * A reusable loading spinner that displays the Summer Squad logo with a slow,
 * smooth rotation animation. Used for full-page loading states.
 * 
 * Automatically detects stuck loading states and suggests browser refresh.
 * 
 * @param {string} [message] - Optional loading message to display below the logo
 * @param {string} [size] - Logo size: 'sm' (48px), 'md' (64px), 'lg' (80px). Default: 'lg'
 * @param {boolean} [isLoading] - Optional loading state. If provided, will monitor for stuck states and show refresh message after 20 seconds. Defaults to true when not provided.
 * @param {boolean} [enableStuckDetection] - Whether to enable stuck loading detection (default: true)
 */
const AppLoadingSpinner = ({ message, size = 'lg', isLoading, enableStuckDetection = true }) => {
  // Monitor for stuck loading states - only if explicitly enabled and isLoading is provided or defaults to true
  // Default to true for backwards compatibility (spinner is shown, so assume it's loading)
  const loadingState = isLoading !== undefined ? isLoading : true;
  useStuckLoadingDetector(loadingState, 20000, enableStuckDetection);
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20'
  };

  const logoSize = sizeClasses[size] || sizeClasses.lg;

  return (
    <div className="flex flex-col items-center justify-center">
      <style>{`
        @keyframes slow-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .logo-spinner {
          animation: slow-spin 2.5s ease-in-out infinite;
        }
      `}</style>
      <div className={`${logoSize} mb-4`}>
        <img
          src={risingSquadLogo}
          alt="Summer Squad"
          className={`${logoSize} logo-spinner object-contain`}
        />
      </div>
      {message && (
        <p className="text-slate-600 text-sm md:text-base">{message}</p>
      )}
    </div>
  );
};

export default AppLoadingSpinner;

